import React, { useMemo, useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import SignaturePad from "react-signature-canvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import userActions from "../../_actions/userActions";
import { USER, USER_PREFERENCES } from "../../_constants/actionConstants";
import AlertModal from "../_common/AlertModal";
import adrActions from "../../_actions/adrActions";
import cem4601PdfActions from "../../_actions/cem4601PdfActions";
import authenticationActions from "../../_actions/authenticationActions";
import { DOCTYPE } from "../../_constants/docTypeConstants";

export default function AdrSubmitSignatureModalV3(props) {
  const { showModal, toggleModal, assistantDailyReportId, authorUserId, tabId, onSign } =
    props;

  const userPref = useSelector(
    (state) => state.authentication.user.userPreferences || null
  );

  const [signature, setSignature] = useState(null);
  const [doNotShowAgain, setDoNotShowAgain] = useState(
    userPref ? userPref.userProfile?.doNotPromptForSignature ?? false : false
  );
  const [showAlert_submitCancel, setShowAlert_submitCancel] = useState(false);
  const toggleAlert_submitCancel = () =>
    setShowAlert_submitCancel(!showAlert_submitCancel);

  const [showAlert_signPadEmpty, setShowAlert_signPadEmpty] = useState(false);
  const toggelAlert_signPadEmpty = () =>
    setShowAlert_signPadEmpty(!showAlert_signPadEmpty);

  const dispatch = useDispatch();

  const fetchingUser = useSelector((state) => state.users.fetching);
  const existSignatureData = useSelector(
    (state) => state.users.miscSessionData.userSignature || null
  );

  const openTabs = useSelector((state) => state.mainTabs.openTabs);
  const userPrefTabExists = useMemo(
    () => (openTabs || []).some((t) => t.docType === DOCTYPE.USER_PREFERENCES),
    [openTabs]
  );

  // Clean up signature on unmount
  useEffect(() => {
    return () =>
      !userPrefTabExists ? dispatch({ type: USER.CLEAR_SIGNATURE_DATA }) : null;
  }, []);

  // Load existing signature when initializing.
  // Signature could have been created in User Preferences module.
  useEffect(() => {
    const getUserSig = async (authorUserId) => {
      const suppressWarning = true;
      await dispatch(userActions.getSignature(authorUserId, suppressWarning));
    };
    getUserSig(authorUserId);
  }, [authorUserId]);

  useEffect(() => {
    if (existSignatureData?.dataUrl) setSignature(existSignatureData.dataUrl);
  }, [existSignatureData]);

  /** @type {React.MutableRefObject<SignaturePad>} */
  const signPadRef = useRef(null);

  const handleClick_clear = (e) => {
    e.preventDefault();
    if (signature) setSignature("");
    else signPadRef.current.clear();
  };

  const handleAlert_cancelSign = () => async () => {
    await dispatch(adrActions.retractSubmission(assistantDailyReportId));
    toggleAlert_submitCancel();
    toggleModal();
  };

  const handleClick_sign = async (e) => {
    e.preventDefault();
    const sigData = {
      dataUrl: signPadRef.current
        ? signPadRef.current.getTrimmedCanvas().toDataURL("image/png")
        : "",
      createdOn: new Date(),
    };

    // If signature pad is shown:
    // 1. If empty, remind user to sign and do not proceed with submit
    // 2. If a signature is drawn but one already exists on file, add signature as
    //    a newer version, then submit
    // 3. If a signature is drawn and no signature exists on file, add signature,
    //    then submit
    // (Case #2 would happen if the existing signature was initially loaded or user
    // clicked "Use Existing", but then proceeded to press "Clear" and enable SignaturePad.)
    if (signPadRef.current) {
      if (signPadRef.current.isEmpty()) {
        toggelAlert_signPadEmpty();
        return;
      } else {
        await dispatch(userActions.addSignature(authorUserId, sigData));
      }
    }
    // If signature pad is not shown, do not proceed if 'signature' is empty.
    else if (!signature) {
      alert("No signature provided.");
      return;
    }

    await dispatch(adrActions.markAsSigned(tabId));

    // Create PDF for submit. Do not await this.
    dispatch(cem4601PdfActions.createPdfForSubmit(assistantDailyReportId));

    // If 'do not show' is checked, update user preferences
    if (doNotShowAgain) {
      dispatch({
        type: USER_PREFERENCES.UPDATE,
        sectionId: "userProfile",
        configId: "doNotPromptForSignature",
        configValue: true,
      });
      dispatch(authenticationActions.saveUserPrefs());
    }

    onSign();
    toggleModal();
  };

  const handleClick_useExisting = async (e) => {
    e.preventDefault();
    const suppressWarning = false;
    await dispatch(userActions.getSignature(authorUserId, suppressWarning));
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <Modal isOpen={showModal} size="md">
      <ModalHeader className="bg-info text-white" close={closeBtn}>
        <FontAwesomeIcon icon="file-signature" className="fa-fw mr-2" />
        Sign Daily Report (Submit)
      </ModalHeader>
      <ModalBody>
        <div className="card">
          <div className="card-body m-2 p-2">
            <div className="form-row form-group mb-2">
              <label htmlFor="signature" className="mb-1">
                Signature is required for this project:
              </label>
              <div className="adr-sign__signpad">
                {!fetchingUser ? (
                  signature ? (
                    <img
                      src={signature}
                      alt="Author Signature"
                      style={{ width: "100%", height: "100%" }}
                    />
                  ) : (
                    <SignaturePad
                      canvasProps={{ style: { width: "100%", height: "100%" } }}
                      ref={(ref) => (signPadRef.current = ref)}
                      onEnd={() => {}}
                    />
                  )
                ) : (
                  "Loading..."
                )}
              </div>
            </div>
            <div className="form-row form-group mb-2 d-flex flex-row">
              <div className="mr-auto">
                <button
                  id="useExisting"
                  name="useExisting"
                  className="btn btn-sm btn-secondary"
                  onClick={handleClick_useExisting}
                >
                  Use Existing
                </button>
              </div>
              <div>
                <button
                  id="clearSignature"
                  name="clearSignature"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClick_clear}
                >
                  Clear
                </button>
              </div>
            </div>
            <hr className="my-3" />
            <div className="form-row form-group mb-0">
              <div className="custom-control-inline custom-control custom-checkbox float-left mr-1">
                <input
                  type="checkbox"
                  name="doNotShowAgain"
                  id="doNotShowAgain"
                  className="custom-control-input my-auto"
                  value={doNotShowAgain}
                  checked={doNotShowAgain}
                  onChange={(e) => setDoNotShowAgain(e.target.checked)}
                />
                <label htmlFor="doNotShowAgain" className="custom-control-label my-auto">
                  Do not ask me again; use this signature in the future
                </label>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-row form-group">
          <button
            id="cancelSubmit"
            name="cancelSubmit"
            className="btn btn-secondary mx-2"
            onClick={toggleAlert_submitCancel}
          >
            Cancel
          </button>
          <button
            id="sign"
            name="sign"
            className="btn btn-primary"
            onClick={handleClick_sign}
            disabled={fetchingUser}
          >
            <b>Accept Signature & Sign</b>
          </button>
        </div>
      </ModalFooter>
      {showAlert_submitCancel && (
        <AlertModal
          alertTitle="Submit Cancelled"
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText={
            "This Daily Report will remain as a draft until you sign for submit."
          }
          acceptCaption={"OK"}
          acceptClass={"secondary"}
          provideReject={false}
          handleAlertAction={handleAlert_cancelSign}
          toggleAlertModal={toggleAlert_submitCancel}
          showAlertModal={showAlert_submitCancel}
        />
      )}
      {showAlert_signPadEmpty && (
        <AlertModal
          alertTitle="Signature Empty"
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText={"Signature pad is empty."}
          acceptCaption={"OK"}
          acceptClass={"secondary"}
          provideReject={false}
          handleAlertAction={toggelAlert_signPadEmpty}
          toggleAlertModal={toggelAlert_signPadEmpty}
          showAlertModal={showAlert_signPadEmpty}
        />
      )}
    </Modal>
  );
}
